// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A warning component for asset inventory date validation.
 * This module provides a visual indicator when a selected
 * date precedes an asset's inventory date, helping prevent
 * data inconsistencies. It handles null cases gracefully
 * and provides clear warning messages.
 *
 * Key features:
 * - Date validation
 * - Visual warning
 * - Tooltip messages
 * - Null handling
 * - Collapsible display
 */
import { SparseAsset } from '@/asset/api/types';
import { FormInputWarning } from '@/components/form-input-warning';
import { DateTime } from 'luxon';

/**
 * Props for configuring the AssetInventoryDateWarning component.
 *
 * @interface Props
 */
interface Props {
  /**
   * An asset with an inventory date.
   * Can be null for cases where asset isn't loaded.
   */
  asset: SparseAsset | null;

  /**
   * A date-time to compare against the asset's inventory date.
   * Can be null when no date is selected.
   */
  dateTime: DateTime | null;
}

/**
 * A warning component for date validation.
 * Shows a warning icon with tooltip when a selected date
 * is before the asset's inventory date. Handles null
 * cases gracefully by hiding the warning.
 *
 * Features:
 * - Date comparison
 * - Warning icon display
 * - Tooltip messages
 * - Null state handling
 * - Collapsible design
 *
 * Usage example:
 * ```tsx
 * function AssetDatePicker({ asset, selectedDate }: Props) {
 *   return (
 *     <Stack direction="row" alignItems="center">
 *       <DatePicker value={selectedDate} />
 *       <AssetInventoryDateWarning
 *         asset={asset}
 *         dateTime={selectedDate}
 *       />
 *     </Stack>
 *   );
 * }
 * ```
 */
export function AssetInventoryDateWarning(props: Props) {
  if (props.asset === null) {
    return null;
  }

  const tooltip = `This date is before the asset's inventory date of ${props.asset.inInventoryDate?.toLocaleString({ dateStyle: 'medium' })}, you may want to double check your dates`;
  const isOpen =
    props.dateTime !== null &&
    props.asset.inInventoryDate !== null &&
    props.dateTime < props.asset.inInventoryDate;

  return <FormInputWarning isOpen={isOpen} tooltip={tooltip} />;
}
